import { Sparkles,Video } from 'lucide-react'
import { useLocation,useNavigate } from 'react-router-dom'

import claudeLogo from '@/assets/claude.svg'
import cursorLogo from '@/assets/cursor.svg'
import openaiLogo from '@/assets/openai.svg'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import IconContainer from '@/components/IconContainer'
import LoadingState from '@/components/LoadingState'
import { PageContainer } from '@/components/PageContainer'
import PageHeader from '@/components/PageHeader'
import RenameDialog from '@/components/RenameDialog'
import { Button } from '@/components/ui/button'
import { useCachedData } from '@/hooks/useCachedData'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useToast } from '@/hooks/useToast'
import { apiFetch } from '@/lib/api'
import { ROUTES } from '@/lib/routes'
import type { Project } from '@/types/domain.types'

import { ProjectCard } from './components/ProjectCard'
import { useProjectActions } from './hooks/useProjectActions'
import { useProjects } from './hooks/useProjects'

interface OAuthConnection {
  id: string
  clientId: string
  clientName: string
  scope: string
  createdAt: number
  lastUsed: number
}

export default function ProjectsPage() {
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const { projects, loading, loadingMore, hasMore, loadMore, setProjects } = useProjects()
  const { success, error: showError } = useToast()

  // Fetch OAuth connections to determine onboarding state
  const { data: connections, loading: connectionsLoading } = useCachedData<OAuthConnection[]>({
    cacheKey: 'oauth-connections',
    maxAge: 300000, // 5 minutes cache
    fetchFn: async () => {
      const response = await apiFetch('/oauth/connections')
      if (!response.ok) {
        if (response.status === 401) {
          return []
        }
        throw new Error('Failed to fetch connections')
      }
      return await response.json()
    },
  })

  const {
    handleRename,
    handleCopyId,
    handleDelete,
    handleExport,
    handleDownload,
    confirmDialog,
    setConfirmDialog,
    confirmDelete,
    renameDialog,
    setRenameDialog,
    performRename,
  } = useProjectActions({ projects, setProjects, onSuccess: success, onError: showError })

  const handlePlay = (project: Project) => {
    // Always navigate to /player/:id route for proper back navigation
    // Pass render URL if available, otherwise player will handle composition rendering
    navigate(ROUTES.player(project.id), { 
      state: { 
        backgroundLocation: routerLocation,
        ...(project.status === 'complete' && project.renderUrl ? { url: project.renderUrl, type: 'video' } : {})
      } 
    })
  }

  const handleEdit = (project: Project) => {
    navigate(ROUTES.editor(project.id))
  }

  // Helper to get chat URL based on client name
  const getChatUrl = (clientName: string): string => {
    const name = clientName.toLowerCase()
    if (name.includes('chatgpt') || name.includes('openai')) return 'https://chatgpt.com/'
    if (name.includes('claude')) return 'https://claude.ai/new'
    if (name.includes('cursor')) return 'cursor://anysphere.cursor-deeplink/createchat'
    return '#'
  }

  // Helper to check if client is desktop-only
  const isDesktopOnly = (clientName: string): boolean => {
    const name = clientName.toLowerCase()
    return name.includes('cursor')
  }

  // Render welcome onboarding (no OAuth connections)
  const renderWelcomeOnboarding = () => {
    const integrations = [
      { id: 'claude', name: 'Claude', logo: claudeLogo },
      { id: 'cursor', name: 'Cursor', logo: cursorLogo },
      { id: 'chatgpt', name: 'ChatGPT', logo: openaiLogo },
    ]

    return (
      <div className="text-center pb-12 max-w-2xl mx-auto px-6 sm:px-0">
        <div className="mx-auto flex justify-center">
          <IconContainer icon={Sparkles} size="md" variant="gradient" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-gray-900 px-2 sm:px-0">
          Welcome to Balboa,<span className="sm:inline block"> the AI-based video editor</span>
        </h3>
        <p className="mt-2 text-sm text-gray-600 px-2 sm:px-0">
          This isn't a video editor like CapCut or Premiere: here you create and edit your video projects by chatting with your favorite{'\u00A0'}AI.
          <br /><br />
          Let's start by linking Balboa to your usual AI{'\u00A0'}service:
        </p>
        <div className="mt-6 flex flex-row gap-2 sm:gap-3 justify-center">
          {integrations.map((integration) => (
            <Button
              key={integration.id}
              onClick={() => navigate(ROUTES.chatSetup(integration.id as 'chatgpt' | 'claude' | 'cursor'), {
                state: { from: ROUTES.projects }
              })}
              variant="outline"
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm flex-1 sm:flex-initial max-w-[110px] sm:max-w-none"
            >
              <img src={integration.logo} alt={integration.name} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" loading="lazy" />
              <span className="truncate">{integration.name}</span>
            </Button>
          ))}
        </div>
      </div>
    )
  }

  // Render start chat onboarding (has OAuth connections)
  const renderStartChatOnboarding = () => {
    if (!connections || connections.length === 0) return null

    // Get the most recently used connection
    const mostRecentConnection = connections.reduce((latest, current) => {
      return (current.lastUsed > latest.lastUsed) ? current : latest
    }, connections[0])

    const clientName = mostRecentConnection.clientName
    const chatUrl = getChatUrl(clientName)
    const isDesktop = isDesktopOnly(clientName)

    return (
      <div className="text-center pb-12 max-w-2xl mx-auto px-6 sm:px-0">
        <div className="mx-auto flex justify-center">
          <IconContainer icon={Video} size="md" variant="gradient" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-gray-900 px-2 sm:px-0">
          Create your first project
        </h3>
        <p className="mt-2 text-sm text-gray-600 px-2 sm:px-0">
          Open a chat with {clientName} and tell it "start Balboa onboarding"
        </p>
        {!isDesktop && (
          <div className="mt-6">
            <Button
              onClick={() => window.open(chatUrl, '_blank')}
              size="sm"
              className="text-xs px-4"
            >
              Start Chat
            </Button>
          </div>
        )}
      </div>
    )
  }

  const hasProjects = projects.length > 0
  const showOnboarding = !hasProjects
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    enabled: !loading && hasProjects,
  })

  if (loading || connectionsLoading) {
    return <LoadingState variant="page" />
  }

  const hasConnections = connections && connections.length > 0

  return (
    <PageContainer>
      {!showOnboarding && (
        <PageHeader 
          title="Projects" 
          description="Create and manage your video projects" 
        />
      )}

      {showOnboarding ? (
        <div className="flex-1 flex items-center justify-center">
          {hasConnections ? renderStartChatOnboarding() : renderWelcomeOnboarding()}
        </div>
      ) : (
        <>
          <div
            className={
              `grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-4` +
              ((hasMore || loadingMore) ? ' pb-6' : ' pb-4')
            }
          >
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onPlay={handlePlay}
                onRename={handleRename}
                onCopyId={handleCopyId}
                onDelete={handleDelete}
                onExport={handleExport}
                onDownload={handleDownload}
                onEdit={handleEdit}
              />
            ))}
          </div>

          {hasMore && <div ref={sentinelRef} className="h-px w-full" aria-hidden="true" />}

          {loadingMore && (
            <div className="flex items-center justify-center py-6">
              <LoadingState variant="inline" />
            </div>
          )}
        </>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.show}
        onClose={() => setConfirmDialog({ show: false })}
        onConfirm={confirmDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${confirmDialog.projectName}"? This action cannot be undone.`}
        variant="destructive"
      />

      <RenameDialog
        open={renameDialog.show}
        initialName={renameDialog.project?.name || ''}
        onSubmit={performRename}
        onCancel={() => setRenameDialog({ show: false })}
      />
    </PageContainer>
  )
}
